
import React, { Suspense, useState, useEffect } from 'react';
import Header from './Header';
import Footer from './Footer';
import NotificationBanner from './NotificationBanner';
import BackgroundStyles from './background/BackgroundStyles';
import SEOHead from './SEOHead';
import CookieConsent from './CookieConsent';
import { useTrafficTracking } from '@/hooks/useTrafficTracking';

// Lazy load background animations to reduce TBT
const BackgroundAnimations = React.lazy(() => import('./background/BackgroundAnimations'));

interface LayoutProps {
  children: React.ReactNode;
  title?: string;
  description?: string;
  keywords?: string;
  ogImage?: string;
  canonical?: string;
  noindex?: boolean;
}

const Layout = ({ 
  children, 
  title, 
  description, 
  keywords, 
  ogImage, 
  canonical, 
  noindex 
}: LayoutProps) => {
  // Enable traffic tracking for all pages
  useTrafficTracking();
  
  // Defer background animations to avoid blocking main thread
  const [showAnimations, setShowAnimations] = useState(false);
  
  useEffect(() => {
    // Use requestIdleCallback if available, otherwise setTimeout
    const deferAnimations = () => {
      setShowAnimations(true);
    };
    
    if ('requestIdleCallback' in window) {
      requestIdleCallback(deferAnimations, { timeout: 1000 });
    } else {
      setTimeout(deferAnimations, 100);
    }
  }, []);
  
  return (
    <div className="min-h-screen bg-slate-900 text-white relative overflow-hidden">
      <SEOHead 
        title={title}
        description={description}
        keywords={keywords}
        ogImage={ogImage}
        canonical={canonical}
        noindex={noindex}
      />
      {showAnimations && (
        <Suspense fallback={null}>
          <BackgroundAnimations />
        </Suspense>
      )}
      
      <div className="relative z-10">
        <Header />
        <NotificationBanner />
        <main className="pt-20">
          {children}
        </main>
        <Footer />
      </div>
      
      <BackgroundStyles />
      <CookieConsent />
    </div>
  );
};

export default Layout;
